// src/components/messaging/MessageItem.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { Message, GroupMessage, MessagingUser } from '../../types/messaging';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import ReactionPicker, { ReactionKey } from './ReactionPicker';
import MessageReactions from './MessageReactions';
import AudioPlayer from './AudioPlayer';

interface MessageItemProps {
  message: Message | GroupMessage;
  isOwn: boolean;
  currentUserId: number;
  sender?: MessagingUser;
  showAvatar?: boolean;
  showName?: boolean;
  onReply?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onReaction?: (reaction: ReactionKey) => void;
  onRemoveReaction?: (reaction: string) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwn,
  currentUserId,
  sender,
  showAvatar = true,
  showName = false,
  onReply,
  onEdit,
  onDelete,
  onReaction,
  onRemoveReaction,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reactionAnchorEl, setReactionAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const reactionPickerOpen = Boolean(reactionAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReply = () => {
    handleMenuClose();
    onReply?.();
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.();
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.();
  };

  // Reaction handlers
  const handleReactionButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setReactionAnchorEl(event.currentTarget);
  };

  const handleReactionPickerClose = () => {
    setReactionAnchorEl(null);
  };

  const handleReactionSelect = (reaction: ReactionKey) => {
    onReaction?.(reaction);
  };

  const handleReactionClick = (reaction: string) => {
    // If user already reacted with this, remove it; otherwise add it
    const hasOwnReaction = message.reactions?.some(
      (r) => r.user_id === currentUserId && r.reaction === reaction
    );
    if (hasOwnReaction) {
      onRemoveReaction?.(reaction);
    } else {
      onReaction?.(reaction as ReactionKey);
    }
  };

  // Get user's current reaction
  const userReaction = message.reactions?.find((r) => r.user_id === currentUserId)?.reaction;

  // Format time
  const formatTime = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  // Get initials
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Status icon
  const renderStatusIcon = () => {
    const status = (message as Message).status;
    if (!isOwn || !status) return null;

    const iconSx = { fontSize: 14, ml: 0.5 };

    switch (status) {
      case 'sending':
        return <CircularProgress size={10} sx={{ ml: 0.5 }} />;
      case 'sent':
        return <DoneIcon sx={{ ...iconSx, color: 'grey.400' }} />;
      case 'delivered':
        return <DoneAllIcon sx={{ ...iconSx, color: 'grey.400' }} />;
      case 'read':
        return <DoneAllIcon sx={{ ...iconSx, color: 'primary.main' }} />;
      case 'failed':
        return <ErrorOutlineIcon sx={{ ...iconSx, color: 'error.main' }} />;
      default:
        return null;
    }
  };

  // Check if message is deleted
  if (message.deleted_at) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isOwn ? 'flex-end' : 'flex-start',
          px: 2,
          py: 0.5,
        }}
      >
        <Box
          sx={{
            maxWidth: '70%',
            py: 1,
            px: 2,
            borderRadius: 2,
            backgroundColor: 'grey.100',
            fontStyle: 'italic',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('messaging.messageDeleted', 'Ce message a été supprimé')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        alignItems: 'flex-end',
        px: 2,
        py: 0.5,
        '&:hover .message-actions': {
          opacity: 1,
        },
      }}
    >
      {/* Avatar (for received messages) */}
      {!isOwn && showAvatar && (
        <Avatar
          src={sender?.avatar_url || undefined}
          sx={{
            width: 32,
            height: 32,
            mr: 1,
            fontSize: '0.75rem',
          }}
        >
          {getInitials(sender?.name)}
        </Avatar>
      )}

      {/* Message bubble */}
      <Box sx={{ maxWidth: '70%', position: 'relative' }}>
        {/* Sender name (for groups) */}
        {!isOwn && showName && sender?.name && (
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              ml: 1.5,
              display: 'block',
              mb: 0.25,
            }}
          >
            {sender.name}
          </Typography>
        )}

        {/* Main bubble */}
        <Box
          sx={{
            py: 1,
            px: 1.5,
            borderRadius: 2.5,
            borderTopRightRadius: isOwn ? 4 : 20,
            borderTopLeftRadius: isOwn ? 20 : 4,
            backgroundColor: isOwn ? 'primary.main' : 'grey.100',
            color: isOwn ? 'white' : 'text.primary',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            position: 'relative',
            transition: 'transform 0.1s ease',
            animation: message._isOptimistic ? 'fadeIn 0.2s ease' : 'none',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* Reply preview - inside the bubble */}
          {message.reply_to && (
            <Box
              sx={{
                backgroundColor: isOwn
                  ? 'rgba(0,0,0,0.15)'
                  : 'rgba(0,0,0,0.05)',
                borderLeft: '3px solid',
                borderLeftColor: isOwn ? 'rgba(255,255,255,0.7)' : 'primary.main',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                mb: 1,
                cursor: 'pointer',
              }}
            >
              {/* Sender name */}
              <Typography
                variant="caption"
                sx={{
                  color: isOwn ? 'rgba(255,255,255,0.95)' : 'primary.main',
                  fontWeight: 600,
                  display: 'block',
                  lineHeight: 1.3,
                }}
              >
                {message.reply_to.sender?.name || 'Utilisateur'}
              </Typography>
              {/* Content - single line with ellipsis */}
              <Typography
                variant="caption"
                sx={{
                  color: isOwn ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }}
              >
                {message.reply_to.type === 'audio' ? '🎤 Message vocal' :
                 message.reply_to.type === 'image' ? '📷 Image' :
                 message.reply_to.type === 'file' ? '📎 Fichier' :
                 message.reply_to.content}
              </Typography>
            </Box>
          )}
          {/* Image content */}
          {message.type === 'image' && message.file_url && (
            <Box
              component="img"
              src={message.thumbnail_url || message.file_url}
              alt={message.file_name || 'Image'}
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 1,
                cursor: 'pointer',
                mb: message.content ? 1 : 0,
              }}
              onClick={() => window.open(message.file_url!, '_blank')}
            />
          )}

          {/* File content */}
          {message.type === 'file' && message.file_url && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                backgroundColor: isOwn ? 'rgba(255,255,255,0.1)' : 'grey.200',
                borderRadius: 1,
                cursor: 'pointer',
                mb: message.content ? 1 : 0,
              }}
              onClick={() => window.open(message.file_url!, '_blank')}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {message.file_name || 'Fichier'}
              </Typography>
            </Box>
          )}

          {/* Audio content */}
          {message.type === 'audio' && message.file_url && (
            <Box sx={{ py: 0.5 }}>
              <AudioPlayer
                src={message.file_url}
                isOwn={isOwn}
                duration={message.duration}
              />
            </Box>
          )}

          {/* Text content */}
          {message.content && (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {message.content}
            </Typography>
          )}

          {/* Time and status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mt: 0.5,
              opacity: 0.7,
            }}
          >
            {message.edited_at && (
              <Typography variant="caption" sx={{ mr: 0.5, fontSize: '0.65rem' }}>
                {t('messaging.edited', 'modifié')}
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
              {formatTime(message.created_at)}
            </Typography>
            {renderStatusIcon()}
          </Box>
        </Box>

        {/* Reactions display */}
        {message.reactions && message.reactions.length > 0 && (
          <MessageReactions
            reactions={message.reactions}
            currentUserId={currentUserId}
            isOwn={isOwn}
            onReactionClick={handleReactionClick}
          />
        )}

        {/* Action buttons (reaction + menu) */}
        <Box
          className="message-actions"
          sx={{
            position: 'absolute',
            top: -4,
            right: isOwn ? 'auto' : -64,
            left: isOwn ? -64 : 'auto',
            display: 'flex',
            gap: 0.25,
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          {/* Reaction button */}
          <IconButton
            size="small"
            onClick={handleReactionButtonClick}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            <AddReactionOutlinedIcon fontSize="small" />
          </IconButton>

          {/* Menu button */}
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Reaction picker */}
        <ReactionPicker
          anchorEl={reactionAnchorEl}
          open={reactionPickerOpen}
          onClose={handleReactionPickerClose}
          onSelect={handleReactionSelect}
          selectedReaction={userReaction}
        />

        {/* Context menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isOwn ? 'left' : 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: isOwn ? 'right' : 'left',
          }}
        >
          <MenuItem onClick={handleReply}>
            <ListItemIcon>
              <ReplyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('messaging.reply', 'Répondre')}</ListItemText>
          </MenuItem>
          {isOwn && message.type === 'text' && (
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('messaging.edit', 'Modifier')}</ListItemText>
            </MenuItem>
          )}
          {isOwn && (
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{t('messaging.delete', 'Supprimer')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Spacer for own messages when no avatar */}
      {!isOwn && !showAvatar && <Box sx={{ width: 40 }} />}
    </Box>
  );
};

export default MessageItem;
